import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  CalendarIcon,
  ChartBarIcon,
  ArrowTopRightOnSquareIcon,
} from '@heroicons/react/24/outline';

function articleMainImageSrc(m) {
  if (!m) return null;
  if (typeof m === 'string') return m.startsWith('http') ? m : `${process.env.REACT_APP_API_URL}${m}`;
  if (m.url) return m.url;
  if (m.legacyPath) return `${process.env.REACT_APP_API_URL}${m.legacyPath}`;
  return null;
}

export default function AdminBlog() {
  const [articles, setArticles] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('tous');
  const { user } = useAuth();
  const navigate = useNavigate();

  const hasAdminRole =
    user &&
    (user.roles || [user.role]).some((r) => ['admin', 'super_admin'].includes(r));

  useEffect(() => {
    if (!hasAdminRole) {
      if (user) navigate('/');
      return;
    }
    fetchArticles();
    fetchStats();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, user, navigate, hasAdminRole]);

  const fetchArticles = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      params.append('limit', '2000');
      params.append('page', '1');
      if (filter !== 'tous') params.append('statut', filter);

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/actualites/articles?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      const list = response.data?.articles;
      setArticles(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error('Erreur lors de la récupération des articles:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/actualites/stats`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setStats(response.data);
    } catch (error) {
      console.error('Erreur lors de la récupération des stats:', error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet article ?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/actualites/articles/${id}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      fetchArticles();
      fetchStats();
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      alert(error.response?.data?.message || 'Erreur lors de la suppression');
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getStatutBadge = (statut) => {
    const badges = {
      'publie': 'bg-green-100 text-green-800',
      'brouillon': 'bg-gray-100 text-gray-800',
      'programme': 'bg-blue-100 text-blue-800'
    };
    const labels = {
      'publie': 'Publié',
      'brouillon': 'Brouillon',
      'programme': 'Programmé'
    };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badges[statut]}`}>
        {labels[statut]}
      </span>
    );
  };

  const getVisibiliteBadge = (visibilite) => {
    const badges = {
      public: 'bg-teal-100 text-teal-800',
      tous: 'bg-purple-100 text-purple-800',
      organisme: 'bg-indigo-100 text-indigo-800',
      SAR: 'bg-honey-100 text-honey-900',
      AMAIR: 'bg-honey-100 text-honey-900',
      ADAR: 'bg-honey-100 text-honey-900',
    };
    const labels = {
      public: 'Public',
      tous: 'Tous les adhérents',
      organisme: 'Par organisme',
      SAR: 'SAR',
      AMAIR: 'AMAIR',
      ADAR: 'ADAR',
    };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badges[visibilite] || 'bg-gray-100 text-gray-800'}`}>
        {labels[visibilite] || visibilite}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-lg text-gray-600">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Gestion des Actualités</h1>
              <p className="mt-1 text-sm text-gray-600">
                Vue unifiée : tous les articles (tous organismes) sont listés ; chaque article reste rattaché à son organisme.
              </p>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <button
                onClick={() => navigate('/actualites')}
                className="inline-flex items-center px-4 py-2 bg-white text-gray-700 text-sm font-medium rounded-md border border-gray-300 hover:bg-gray-50 transition-colors"
              >
                <ArrowTopRightOnSquareIcon className="h-4 w-4 mr-2" />
                Vue publique
              </button>
              <button
                onClick={() => navigate('/admin/actualites/nouveau')}
                className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700 transition-colors"
              >
                <PlusIcon className="h-5 w-5 mr-2" />
                Nouvel article
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <ChartBarIcon className="h-8 w-8 text-gray-400" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Total</p>
                  <p className="text-2xl font-semibold text-gray-900">{stats.total}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="h-8 w-8 rounded-full bg-green-100 flex items-center justify-center">
                  <div className="h-3 w-3 rounded-full bg-green-600"></div>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Publiés</p>
                  <p className="text-2xl font-semibold text-gray-900">{stats.publies}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center">
                  <div className="h-3 w-3 rounded-full bg-gray-600"></div>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Brouillons</p>
                  <p className="text-2xl font-semibold text-gray-900">{stats.brouillons}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                  <div className="h-3 w-3 rounded-full bg-blue-600"></div>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Programmés</p>
                  <p className="text-2xl font-semibold text-gray-900">{stats.programmes}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <EyeIcon className="h-8 w-8 text-gray-400" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Vues totales</p>
                  <p className="text-2xl font-semibold text-gray-900">{stats.vuesTotal}</p>
                </div>
              </div>
            </div>
          </div>
        )}
        {stats && stats.autres > 0 && (
          <p className="text-sm text-honey-800 mb-4 px-1">
            {stats.autres} article(s) ont un statut inattendu en base (non compté dans Publiés / Brouillons / Programmés). Vérifiez les données si besoin.
          </p>
        )}

        {/* Filters */}
        <div className="bg-white rounded-lg shadow mb-6 p-4">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-700">Filtrer :</span>
            {['tous', 'publie', 'brouillon', 'programme'].map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                  filter === f
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {f === 'tous' ? 'Tous' : f === 'publie' ? 'Publiés' : f === 'brouillon' ? 'Brouillons' : 'Programmés'}
              </button>
            ))}
          </div>
        </div>

        {/* Articles List */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {articles.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500">Aucun article trouvé</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Article
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">
                    Organisme
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                    Visibilité
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                    Vues
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {articles.map((article) => (
                  <tr key={article._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        {articleMainImageSrc(article.imagePrincipale) ? (
                          <img
                            src={articleMainImageSrc(article.imagePrincipale)}
                            alt={article.titre}
                            className="h-10 w-10 rounded object-cover"
                          />
                        ) : (
                          <div className="h-10 w-10 rounded bg-gray-200 flex items-center justify-center">
                            <span className="text-gray-400 text-xs">IMG</span>
                          </div>
                        )}
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{article.titre}</div>
                          <div className="text-sm text-gray-500">
                            Par {article.auteur?.prenom} {article.auteur?.nom}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 hidden sm:table-cell">
                      {article.organisme || '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatutBadge(article.statut)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap hidden md:table-cell">
                      {getVisibiliteBadge(article.visibilite)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap hidden sm:table-cell">
                      <div className="flex items-center text-sm text-gray-500">
                        <CalendarIcon className="h-4 w-4 mr-1" />
                        {formatDate(article.datePublication || article.createdAt)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap hidden md:table-cell">
                      <div className="flex items-center text-sm text-gray-500">
                        <EyeIcon className="h-4 w-4 mr-1" />
                        {article.vues}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => navigate(`/actualites/${article.slug}`)}
                          className="text-indigo-600 hover:text-indigo-900"
                          title="Voir"
                        >
                          <EyeIcon className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => navigate(`/admin/actualites/edit/${article._id}`)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Modifier"
                        >
                          <PencilIcon className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleDelete(article._id)}
                          className="text-red-600 hover:text-red-900"
                          title="Supprimer"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
